class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.removeButtons = Array.from(this.querySelectorAll('button, a')) || [];
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.setLoading(true);
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
  setLoading(isLoading) {
    const loading = !!isLoading;
    this.classList.toggle('is-loading', loading);
    (this.removeButtons || []).forEach((btn) => {
      if (!btn) return;
      if (btn.tagName === 'BUTTON') {
        btn.toggleAttribute('disabled', loading);
      } else if (btn.tagName === 'A') {
        if (loading) {
          btn.setAttribute('aria-disabled', 'true');
          btn.style.pointerEvents = 'none';
        } else {
          btn.removeAttribute('aria-disabled');
          btn.style.pointerEvents = '';
        }
      }
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => this.onChange(event), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;
  _updateInFlight = false; // SINGLE-FLIGHT MUTEX

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      return this.onCartUpdate();
    });
  }
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }
  setValidity(event, index, message) {
    const target = event.target;
    target.setCustomValidity(message);
    if (typeof target.reportValidity === 'function') target.reportValidity();
    this.resetQuantityInput(index);
    if (typeof target.select === 'function') target.select();
  }
  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0 && Number.isNaN(parseInt(event.target.step))) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement && document.activeElement.getAttribute
          ? document.activeElement.getAttribute('name')
          : '',
        event.target.dataset.quantityVariantId
      );
    }
  }
  onChange(event) { this.validateQuantity(event); }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((htmlText) => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
          }
          document.dispatchEvent(new CustomEvent('recently-viewed:refresh', { detail: { source: 'cart-items-update' } }));
        })
        .catch(console.error);
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((r) => r.text())
        .then((htmlText) => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch(console.error);
    }
  }

  getSectionsToRender() {
    return [
      { id: 'main-cart-items', section: document.getElementById('main-cart-items').dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'main-cart-footer', section: document.getElementById('main-cart-footer').dataset.id, selector: '.js-contents' },
    ];
  }

  async updateQuantity(line, quantity, event, name, variantId) {
    if (this._updateInFlight) return;      // DROP concurrent updates
    this._updateInFlight = true;

    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);
    const sections = this.getSectionsToRender().map((s) => s.section);

    let endpoint = routes.cart_change_url;
    let body;

    if (variantId !== undefined && variantId !== null && variantId !== '') {
      // swap variant: /cart/update.js { updates: { oldId:0, newId:qty } }
      const target = event && event.target ? event.target : null;
      let currentId = null;
      if (target) {
        const ds = target.dataset || {};
        currentId = ds.currentVariantId || ds.quantityVariantId || null;
      }
      if (!currentId) {
        const row = (target && target.closest && target.closest('tr.cart-item')) || null;
        if (row) {
          const jsonEl = row.querySelector('script.js-cart-item-variants');
          if (jsonEl) {
            try {
              const variants = JSON.parse(jsonEl.textContent || '[]');
              const selects = row.querySelectorAll('[data-cart-variant-option-select]');
              if (selects.length) {
                const selected = Array.from(selects)
                  .map((el) => ({ el, pos: parseInt(el.dataset.optionPosition || '0', 10) }))
                  .sort((a, b) => a.pos - b.pos)
                  .map(({ el }) => el.value);
                const match = variants.find((v) => v.options && v.options.every((o, i) => String(o) === String(selected[i])));
                if (match) currentId = match.id;
              }
            } catch (_) {}
          }
        }
      }
      const newId = parseInt(String(variantId).trim(), 10);
      const updates = {};
      if (currentId) updates[String(currentId)] = 0;
      updates[String(newId)] = quantity;

      endpoint = routes.cart_update_url;
      body = JSON.stringify({ updates, sections, sections_url: window.location.pathname });
    } else {
      body = JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname });
    }

    try {
      const resText = await fetch(`${endpoint}`, { ...fetchConfig(), ...{ body } }).then((r) => r.text());
      const parsedState = JSON.parse(resText);

      CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && quantityElement && updatedValue !== parseInt(quantityElement.value)) {
          message = typeof updatedValue === 'undefined'
            ? window.cartStrings.error
            : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          const target = lineItem.querySelector(`[name="${name}"]`);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, target) : target.focus();
        } else {
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        }
      });

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
    } catch (err) {
      this.querySelectorAll('.loading__spinner').forEach((o) => o.classList.add('hidden'));
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
      console.error(err);
    } finally {
      this.disableLoading(line);
      CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      this._updateInFlight = false; // RELEASE MUTEX
    }
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);
    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
  }
  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
  // Performance: Cache cart items container to avoid repeated DOM queries
  getCartItemsContainer() {
    if (!this._cartItemsContainer) {
      this._cartItemsContainer = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    }
    return this._cartItemsContainer;
  }

  enableLoading(line) {
    const mainCartItems = this.getCartItemsContainer();
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((o) => o.classList.remove('hidden'));
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = this.getCartItemsContainer();
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((o) => o.classList.add('hidden'));
    const removeButtons = this.querySelectorAll(`#Remove-${line}, #CartDrawer-Remove-${line}`);
    removeButtons.forEach((btn) => { if (btn && typeof btn.setLoading === 'function') btn.setLoading(false); });
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        'input',
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
            CartPerformance.measureFromEvent('note-update:user-action', event)
          );
        }, ON_CHANGE_DEBOUNCE_TIMER)
      );
    }
  });
}

/* ---------- PROMO CODE (unchanged) ---------- */
(() => {
  function getDiscountInput() { return document.getElementById('CartDrawer-DiscountCode'); }
  function getDiscountCode() { const i = getDiscountInput(); return (i && i.value ? i.value.trim() : ''); }
  function updateCheckoutButtonDataset() {
    const code = getDiscountCode();
    const checkoutBtn = document.getElementById('CartDrawer-Checkout');
    if (checkoutBtn) checkoutBtn.dataset.discount = code;
  }
  document.addEventListener('input', (e) => {
    if (e.target && e.target.id === 'CartDrawer-DiscountCode') updateCheckoutButtonDataset();
  });
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest('[data-apply-discount]');
    if (!btn) return;
    e.preventDefault();
    updateCheckoutButtonDataset();
    const code = getDiscountCode();
    const status = document.getElementById('CartDrawer-DiscountStatus');
    if (status) status.textContent = code ? 'Will apply at checkout' : '';
  });
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form && form.id === 'CartDrawer-Form')) return;
    const code = getDiscountCode();
    if (code) {
      e.preventDefault();
      try {
        window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
      } catch (err) {
        // If navigation fails, allow default form submission
        console.error('Error applying discount code:', err);
        form.submit();
      }
    }
  });
})();

/* ---------- VARIANT SWITCHING: SINGLE, DEDUPED HANDLERS ---------- */

// Utilities
function computeAndApplyOptionAvailability(row) {
  if (!row) return;
  const jsonEl = row.querySelector('script.js-cart-item-variants');
  if (!jsonEl) return;
  let variants = [];
  try { variants = JSON.parse(jsonEl.textContent || '[]'); } catch (_) { return; }

  const selects = Array.from(row.querySelectorAll('[data-cart-variant-option-select]'))
    .map((el) => ({ el, pos: parseInt(el.dataset.optionPosition, 10) }))
    .sort((a, b) => a.pos - b.pos);

  if (!selects.length) return;

  const selected = selects.map(({ el }) => el.value);
  const available = variants.filter((v) => v && v.available);

  selects.forEach(({ el }, idx) => {
    const otherSelections = selected.slice();
    const options = Array.from(el.options);
    options.forEach((opt) => {
      otherSelections[idx] = opt.value;
      const possible = available.some((v) => {
        if (!v.options || v.options.length !== otherSelections.length) return false;
        for (let i = 0; i < v.options.length; i++) {
          if (String(v.options[i]) !== String(otherSelections[i])) return false;
        }
        return true;
      });
      opt.disabled = !possible;
      if (opt.disabled) opt.setAttribute('aria-disabled', 'true');
      else opt.removeAttribute('aria-disabled');
    });
  });
}

function initCartDrawerOptionAvailability(root = document) {
  const rows = root.querySelectorAll('cart-drawer-items tr.cart-item, cart-items tr.cart-item');
  rows.forEach((row) => computeAndApplyOptionAvailability(row));
}
document.addEventListener('DOMContentLoaded', () => initCartDrawerOptionAvailability());
subscribe && subscribe(PUB_SUB_EVENTS.cartUpdate, () => { initCartDrawerOptionAvailability(); });

/* ONE capture-phase listener to handle BOTH controls and stop propagation */
document.addEventListener('change', (e) => {
  const select = e.target.closest('[data-cart-variant-select],[data-cart-variant-option-select]');
  if (!select) return;

  // Don’t let the quantity validator see this event
  e.stopPropagation();

  const wrapper = select.closest('cart-drawer-items, cart-items');
  if (!wrapper) return;

  const line = parseInt(select.dataset.line, 10);
  const quantity = parseInt(select.dataset.quantity, 10) || 1;
  if (!line || !quantity) return;

  // Per-option selects: resolve options -> variant id
  if (select.matches('[data-cart-variant-option-select]')) {
    const row = select.closest('tr.cart-item');
    computeAndApplyOptionAvailability(row);

    const selects = Array.from(row.querySelectorAll('[data-cart-variant-option-select]'))
      .map((el) => ({ el, pos: parseInt(el.dataset.optionPosition, 10) }))
      .sort((a, b) => a.pos - b.pos)
      .map(({ el }) => el);
    const selectedOptions = selects.map((s) => s.value);

    const jsonEl = row.querySelector('script.js-cart-item-variants');
    if (!jsonEl) return;
    let variants;
    try { variants = JSON.parse(jsonEl.textContent || '[]'); } catch (err) { console.error('variants JSON', err); return; }

    const match = variants.find((v) => {
      if (!v || !v.options) return false;
      if (v.options.length !== selectedOptions.length) return false;
      for (let i = 0; i < v.options.length; i++) {
        if (String(v.options[i]) !== String(selectedOptions[i])) return false;
      }
      return true;
    });

    const currentId = parseInt(select.dataset.currentVariantId || '0', 10);
    const name = select.getAttribute('name') || `options[${select.dataset.optionPosition}]`;
    const errorEl = row.querySelector('[id^="CartDrawer-LineItemError-"] .cart-item__error-text');

    if (!match) { if (errorEl) errorEl.textContent = 'This combination is unavailable.'; return; }
    if (!match.available) { if (errorEl) errorEl.textContent = 'Selected variant is sold out.'; return; }
    if (errorEl) errorEl.textContent = '';
    if (currentId && match.id === currentId) return;

    wrapper.updateQuantity(line, quantity, e, name, match.id);
    return;
  }

  // Simple select with variant id
  const variantId = select.value;
  if (!variantId) return;
  const name = select.getAttribute('name') || 'id';
  wrapper.updateQuantity(line, quantity, e, name, variantId);
}, true);